/**
 * Dynamic form renderer component
 * Takes a FormSchema and renders a fully interactive, validated form
 * Uses react-hook-form + Zod for validation, shadcn/ui Form components for rendering
 */

import { forwardRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { safeZodResolver, onFormValidationError } from "@/utils/safe-zod-resolver";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import type { FormSchema, FormField as FormFieldType } from "@/types/agent-ui-types";
import {
  buildValidationSchema,
  getDefaultValues,
} from "./build-validation-schema";
import {
  TextFieldRenderer,
  EmailFieldRenderer,
  NumberFieldRenderer,
  TextareaFieldRenderer,
  SelectFieldRenderer,
  RadioFieldRenderer,
  CheckboxFieldRenderer,
  ToggleFieldRenderer,
  FileFieldRenderer,
  FilesFieldRenderer,
  DateFieldRenderer,
} from "./field-renderers";

interface FormRendererProps {
  /** The form schema to render */
  schema: FormSchema;
  /** Callback when form is submitted with valid data */
  onSubmit: (values: Record<string, unknown>) => void | Promise<void>;
  /** Whether the form is currently submitting */
  isSubmitting?: boolean;
  /** Optional CSS class for the form element */
  className?: string;
  /** Ref for the form element */
  formRef?: React.RefObject<HTMLFormElement>;
}

/**
 * Maps a field type to its renderer component
 */
function getFieldRenderer(fieldDef: FormFieldType) {
  switch (fieldDef.type) {
    case "text":
      return TextFieldRenderer;
    case "email":
      return EmailFieldRenderer;
    case "number":
      return NumberFieldRenderer;
    case "textarea":
      return TextareaFieldRenderer;
    case "select":
      return SelectFieldRenderer;
    case "radio":
      return RadioFieldRenderer;
    case "checkbox":
      return CheckboxFieldRenderer;
    case "toggle":
      return ToggleFieldRenderer;
    case "file":
      return FileFieldRenderer;
    case "files":
      return FilesFieldRenderer;
    case "date":
      return DateFieldRenderer;
    default:
      return TextFieldRenderer;
  }
}

/**
 * Evaluates whether a field should be visible based on its conditional rule
 */
function isFieldVisible(
  fieldDef: FormFieldType,
  formValues: Record<string, unknown>
): boolean {
  if (!fieldDef.conditional) return true;

  const { field: targetField, operator, value: targetValue } =
    fieldDef.conditional;
  const currentValue = formValues[targetField];

  switch (operator) {
    case "equals":
      return currentValue === targetValue;
    case "notEquals":
      return currentValue !== targetValue;
    case "contains":
      return (
        typeof currentValue === "string" &&
        typeof targetValue === "string" &&
        currentValue.includes(targetValue)
      );
    case "greaterThan":
      return Number(currentValue) > Number(targetValue);
    case "lessThan":
      return Number(currentValue) < Number(targetValue);
    default:
      return true;
  }
}

/**
 * Renders a complete form from a FormSchema definition
 * Handles validation, conditional visibility, and submission
 */
export const FormRenderer = forwardRef<HTMLFormElement, FormRendererProps>(
  function FormRenderer({ schema, onSubmit, isSubmitting = false, className, formRef }, ref) {
    const sortedFields = useMemo(
      () => [...schema.fields].sort((a, b) => a.order - b.order),
      [schema.fields]
    );

    const validationSchema = useMemo(
      () => buildValidationSchema(sortedFields),
      [sortedFields]
    );

    const defaultValues = useMemo(
      () => getDefaultValues(sortedFields),
      [sortedFields]
    );

    const form = useForm({
      resolver: safeZodResolver(validationSchema),
      defaultValues,
      mode: "onBlur",
    });

    const formValues = form.watch();

    // Set the form ref when the form element is available
    const setFormRef = (element: HTMLFormElement | null) => {
      if (ref) {
        (ref as React.MutableRefObject<HTMLFormElement | null>).current = element;
      }
      if (formRef) {
        formRef.current = element;
      }
    };

    return (
      <Form {...form}>
        <form
          ref={setFormRef}
          onSubmit={form.handleSubmit(onSubmit, onFormValidationError)}
          className={className}
        >
          {/* Form fields - scrollable */}
          <div className="space-y-5">
            {sortedFields.map((fieldDef) => {
              if (!isFieldVisible(fieldDef, formValues)) return null;

              const Renderer = getFieldRenderer(fieldDef);

              return (
                <FormField
                  key={fieldDef.id}
                  control={form.control}
                  name={fieldDef.id}
                  render={({ field: rhfField }) => (
                    <FormItem>
                      <FormLabel>
                        {fieldDef.label}
                        {fieldDef.required && (
                          <span className="text-destructive ml-1">*</span>
                        )}
                      </FormLabel>
                      <FormControl>
                        <Renderer field={fieldDef} rhfField={rhfField} />
                      </FormControl>
                      {fieldDef.helpText && (
                        <FormDescription>{fieldDef.helpText}</FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              );
            })}
          </div>

          {/* Hidden submit button for form submission via footer button */}
          <button type="submit" className="hidden" />
        </form>
      </Form>
    );
  }
);

FormRenderer.displayName = "FormRenderer";
